import { ClaimProposalDTOSourceEnum, ClaimResponseDeadlineExtensionDTO } from '@reposit/api-client';
import moment from 'moment';
import React from 'react';
import styled from 'styled-components';

import {
  formatDate,
  formatEnum,
  getResponseDeadlineExtensionReasonLabel,
} from '../../constants/claim-response-deadline-extension';
import { ClaimProposalEntity } from '../../redux/entities/entities.types';
import Button from '../Button';
import {
  CloseButton,
  DetailLabel,
  DetailTableRow,
  DetailValue,
  ModalHeader,
  ModalTitle,
  SECTION_GAP,
  SectionPanel,
  SectionTitle,
  TwoColumnDetailTable,
} from '../DetailsModal';
import IdentifierValue from '../IdentifierValue';
import Modal from '../Modal';
import RepositActorLink from '../Reposit/RepositActorLink';
import { P2 } from '../Typography';

interface ResponseDeadlineDetailsModalProps {
  deadlineLabel: string;
  proposal: ClaimProposalEntity;
  extensions: ClaimResponseDeadlineExtensionDTO[];
  onDismiss: () => void;
  // Only given when the extensions could not be loaded.
  onRetryExtensions?: () => void;
  // Only given for the deadline that can be extended, when the user may extend it.
  onExtend?: () => void;
}

interface DetailRow {
  label: string;
  value: React.ReactNode;
}

const Section = styled.section`
  & + & {
    margin-top: ${SECTION_GAP};
  }
`;

const ActionBar = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 16px;

  button {
    margin: 0;
  }
`;

const DetailsField = styled.dl`
  border-bottom: 1px solid ${(props) => props.theme.colors.dividerLight};
  margin: 0;
  padding: 8px 0 10px;
`;

const DetailsFieldLabel = styled.dt`
  color: ${(props) => props.theme.colors.body2};
  font-family: ${(props) => props.theme.typography.face.secondary};
  font-size: 15px;
  margin-bottom: 8px;
`;

const DetailsFieldValue = styled.dd`
  color: ${(props) => props.theme.colors.body};
  font-family: ${(props) => props.theme.typography.face.secondary};
  font-size: 15px;
  font-weight: bold;
  line-height: 1.45;
  margin: 0;
  overflow-wrap: anywhere;
  white-space: pre-wrap;
`;

const ExtensionPanel = styled(SectionPanel)`
  & + & {
    margin-top: 16px;
  }

  ${DetailsField} {
    border-bottom: none;
  }
`;

const EmptyNote = styled(P2)`
  color: ${(props) => props.theme.colors.body2};
  font-style: italic;
`;

const formatDateTime = (date?: Date | string) => (date ? moment(date).format('DD/MM/YYYY HH:mm:ss') : '-');
const identifier = (id: string) => <IdentifierValue title={id}>{id}</IdentifierValue>;

const DetailRows: React.FC<{ rows: DetailRow[] }> = ({ rows }) => (
  <TwoColumnDetailTable>
    {rows.map((row) => (
      <DetailTableRow key={row.label}>
        <DetailLabel>{row.label}</DetailLabel>
        <DetailValue>{row.value}</DetailValue>
      </DetailTableRow>
    ))}
  </TwoColumnDetailTable>
);

const deadlineRows = (proposal: ClaimProposalEntity): DetailRow[] => [
  { label: 'Proposal ID', value: identifier(proposal.id) },
  { label: 'Proposal', value: `${formatEnum(proposal.type)} · round ${proposal.round}` },
  { label: 'Status', value: formatEnum(proposal.status) },
  // A supplier proposal awaits the tenants; a tenant counter awaits the supplier.
  { label: 'Responding', value: proposal.source === ClaimProposalDTOSourceEnum.SUPPLIER ? 'Tenant(s)' : 'Supplier' },
  { label: 'Response deadline', value: formatDate(proposal.responseDeadline) },
  { label: 'Published at', value: formatDateTime(proposal.publishedAt) },
  { label: 'Auto-responded', value: proposal.autoResponded ? 'Yes' : 'No' },
];

const extensionRows = (extension: ClaimResponseDeadlineExtensionDTO): DetailRow[] => [
  { label: 'ID', value: identifier(extension.id) },
  { label: 'Extended at', value: formatDateTime(extension.createdAt) },
  { label: 'Previous deadline', value: formatDate(extension.previousResponseDeadline) },
  { label: 'New deadline', value: formatDate(extension.responseDeadline) },
  { label: 'Extended by', value: <RepositActorLink user={extension.user} /> },
  { label: 'Auto-response undone', value: extension.autoResponseReversed ? 'Yes' : 'No' },
  { label: 'Reason', value: getResponseDeadlineExtensionReasonLabel(extension.reasonType) },
];

export const ResponseDeadlineDetailsModal: React.FC<ResponseDeadlineDetailsModalProps> = ({
  deadlineLabel,
  proposal,
  extensions,
  onDismiss,
  onRetryExtensions,
  onExtend,
}) => (
  <Modal onDismiss={onDismiss} maxWidth="1040px" scrollable capHeight>
    <ModalHeader>
      <ModalTitle>Deadline details</ModalTitle>
      <CloseButton onClick={onDismiss} aria-label="Close deadline details">
        &times;
      </CloseButton>
    </ModalHeader>

    <Section>
      <SectionTitle>{deadlineLabel}</SectionTitle>
      <DetailRows rows={deadlineRows(proposal)} />
    </Section>

    <Section>
      <SectionTitle>Extensions</SectionTitle>
      {onRetryExtensions ? (
        <EmptyNote>
          The extensions could not be loaded.{' '}
          <Button noArrow={true} mini="true" onClick={onRetryExtensions} type="button">
            Retry
          </Button>
        </EmptyNote>
      ) : extensions.length === 0 ? (
        <EmptyNote>This deadline has not been extended.</EmptyNote>
      ) : null}
      {extensions.map((extension) => (
        <ExtensionPanel key={extension.id}>
          <SectionTitle>{`${formatDate(extension.previousResponseDeadline)} → ${formatDate(extension.responseDeadline)}`}</SectionTitle>
          <DetailRows rows={extensionRows(extension)} />
          <DetailsField>
            <DetailsFieldLabel>Details</DetailsFieldLabel>
            <DetailsFieldValue>{extension.reason}</DetailsFieldValue>
          </DetailsField>
        </ExtensionPanel>
      ))}
      {onExtend ? (
        <ActionBar>
          <Button noArrow={true} mini="true" onClick={onExtend} type="button">
            Add extension
          </Button>
        </ActionBar>
      ) : null}
    </Section>
  </Modal>
);
